import { useEffect, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { useAuthStore } from '../stores/authStore';
import { useMeetingStore } from '../stores/meetingStore';
import { useSettingsStore } from '../stores/settingsStore';
import { useMediaPermissions } from '../hooks/useMediaPermissions';
import {
  ConnectingState,
  ErrorState,
  MeetingContent,
} from './MeetingPageLayout';
import { PermissionsPrompt } from '../components/meeting/PermissionsPrompt';

/**
 * Meeting room page.
 */
export default function MeetingPage() {
  const { roomCode } = useParams<{ roomCode: string }>();
  const navigate = useNavigate();
  const { user } = useAuthStore();
  const settings = useSettingsStore();
  const { room, isConnected, isConnecting, error, joinRoom, clearError } = useMeetingStore();

  const {
    status,
    request,
    audioGranted,
    videoGranted,
    hasAudioDevice,
    hasVideoDevice,
  } = useMediaPermissions(settings.loadDevices);
  const [joinWithoutMedia, setJoinWithoutMedia] = useState(false);
  const canJoin = status === 'granted' || status === 'no-devices' || joinWithoutMedia;
  const allowAudio = audioGranted && !joinWithoutMedia;
  const allowVideo = videoGranted && !joinWithoutMedia;
  useAutoJoin({
    roomCode,
    user,
    canJoin,
    allowAudio,
    allowVideo,
    isConnected,
    isConnecting,
    error,
    joinRoom,
    settings,
  });
  useErrorRedirect(isConnected, isConnecting, error, navigate, clearError);

  if (!canJoin) {
    return (
      <PermissionsPrompt
        status={status}
        hasAudioDevice={hasAudioDevice}
        hasVideoDevice={hasVideoDevice}
        onRequest={request}
        onContinueWithoutMedia={() => setJoinWithoutMedia(true)}
      />
    );
  }

  if (isConnecting) {
    return <ConnectingState />;
  }

  if (error) {
    return <ErrorState error={error} />;
  }

  return (
    <MeetingContent
      roomCode={roomCode || ''}
      roomName={room?.roomName || 'Meeting'}
    />
  );
}

interface AutoJoinOptions {
  roomCode?: string;
  user: { displayName: string; avatarUrl?: string } | null;
  canJoin: boolean;
  allowAudio: boolean;
  allowVideo: boolean;
  isConnected: boolean;
  isConnecting: boolean;
  error: string | null;
  joinRoom: (options: {
    roomCode: string;
    displayName: string;
    audioEnabled: boolean;
    videoEnabled: boolean;
    avatarUrl?: string;
    isVR?: boolean;
    microphoneDeviceId?: string;
    cameraDeviceId?: string;
    speakerDeviceId?: string;
  }) => Promise<void>;
  settings: {
    isMuted: boolean;
    isCameraOn: boolean;
    microphoneDeviceId: string;
    cameraDeviceId: string;
    speakerDeviceId: string;
  };
}

function useAutoJoin({
  roomCode,
  user,
  canJoin,
  allowAudio,
  allowVideo,
  isConnected,
  isConnecting,
  error,
  joinRoom,
  settings,
}: AutoJoinOptions): void {
  useEffect(() => {
    if (
      !roomCode ||
      !user ||
      !canJoin ||
      isConnected ||
      isConnecting ||
      error
    ) {
      return;
    }

    joinRoom({
      roomCode,
      displayName: user.displayName,
      audioEnabled: allowAudio && !settings.isMuted,
      videoEnabled: allowVideo && settings.isCameraOn,
      avatarUrl: user.avatarUrl,
      isVR: false,
      microphoneDeviceId: allowAudio ? settings.microphoneDeviceId : undefined,
      cameraDeviceId: allowVideo ? settings.cameraDeviceId : undefined,
      speakerDeviceId: allowAudio ? settings.speakerDeviceId : undefined,
    }).catch(() => {
      // Error handled by store
    });
  }, [
    roomCode,
    user,
    canJoin,
    allowAudio,
    allowVideo,
    isConnected,
    isConnecting,
    error,
    joinRoom,
    settings,
  ]);
}

function useErrorRedirect(
  isConnected: boolean,
  isConnecting: boolean,
  error: string | null,
  navigate: (path: string) => void,
  clearError: () => void
): void {
  useEffect(() => {
    if (!isConnected && !isConnecting && error) {
      const timer = setTimeout(() => {
        clearError();
        navigate('/');
      }, 3000);

      return () => clearTimeout(timer);
    }
  }, [isConnected, isConnecting, error, navigate, clearError]);
}
